import { useEffect, useRef, useState } from 'react';
import { Check, FolderPlus, Loader2, Palette, Paperclip, Pencil, Trash2, X } from 'lucide-react';
import IconoCarpeta from './IconoCarpeta';
import { ModalConfirmar } from '../ModalConfirmar';
import {
  COLORES_DE_CARPETA,
  colorDeCarpeta,
  type Moodboard,
} from '../../pages/v2/moodboardsApi';

/**
 * La grilla de carpetas de moodboards —
 * ADR-2026-09-13-moodboards-carpetas-de-referencia.
 *
 * Sólo dibuja y avisa: no habla con la base. Quien la monta
 * (`MiMarcaMoodboardsTab`) es el dueño de los datos, así puede refrescar la
 * grilla y la vista de adentro con una sola fuente.
 */

/** Cómo se lee el contador debajo del nombre. */
const contador = (n: number) =>
  n === 0 ? 'Vacía' : n === 1 ? '1 foto' : `${n} fotos`;

/** Medida de la grilla, compartida por las carpetas y la card de crear. */
const GRILLA = 'grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 xl:grid-cols-4';

export const GrillaDeCarpetas = ({
  carpetas,
  onAbrir,
  onCrear,
  onRenombrar,
  onCambiarColor,
  onEliminar,
  grillaClassName = GRILLA,
  onAdjuntar,
  adjuntada,
  rotuloAdjuntada = () => 'En el chat',
  tituloAdjuntar = 'Adjuntar la carpeta entera al chat',
}: {
  carpetas: Moodboard[];
  /** Adjuntar la carpeta ENTERA al chat: sólo lo pasa la biblioteca del Agente
   *  (ADR-2026-09-13-el-agente-ve-los-moodboards). Con `adjuntada` se esconde el
   *  botón de la que ya está en la caja y se la marca. */
  onAdjuntar?: (carpeta: Moodboard) => void;
  adjuntada?: (id: string) => boolean;
  /** Qué dice la marca de la adjuntada y el botón de adjuntar. Por defecto, los del chat
   *  ("En el chat"); el selector de fuentes, donde todavía no se agregó nada, dice qué se
   *  ELIGIÓ (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes-2). */
  rotuloAdjuntada?: (carpeta: Moodboard) => string;
  tituloAdjuntar?: string;
  /** La medida de la grilla. El default es la del tab de Mi marca; la biblioteca del
   *  Agente, que es una columna angosta, pasa la suya
   *  (ADR-2026-09-13-los-moodboards-entran-a-la-biblioteca-del-agente). */
  grillaClassName?: string;
  onAbrir: (carpeta: Moodboard) => void;
  onCrear: (nombre: string) => Promise<void>;
  onRenombrar: (id: string, nombre: string) => Promise<void>;
  onCambiarColor: (id: string, color: string) => Promise<void>;
  onEliminar: (id: string) => Promise<void>;
}) => {
  // Alta: la card fantasma se convierte en un input en el lugar. Sin modal —
  // ponerle nombre a una carpeta es una palabra, y un diálogo para una palabra
  // es más ceremonia que la acción.
  const [creando, setCreando] = useState(false);
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [guardando, setGuardando] = useState(false);

  const [renombrandoId, setRenombrandoId] = useState<string | null>(null);
  const [borrador, setBorrador] = useState('');

  const [aBorrar, setABorrar] = useState<Moodboard | null>(null);
  const [borrando, setBorrando] = useState(false);

  /** Qué carpeta tiene la paleta abierta. Una sola a la vez. */
  const [paletaDe, setPaletaDe] = useState<string | null>(null);

  const inputAlta = useRef<HTMLInputElement | null>(null);
  const inputRenombre = useRef<HTMLInputElement | null>(null);
  const paleta = useRef<HTMLDivElement | null>(null);

  // La paleta se cierra al tocar afuera o con Escape, como cualquier popover de la
  // app. `mousedown` y no `click`: con `click` el mismo toque que la abre volvía a
  // llegar acá y la cerraba en el acto.
  useEffect(() => {
    if (!paletaDe) return;
    const afuera = (e: MouseEvent) => {
      if (!paleta.current?.contains(e.target as Node)) setPaletaDe(null);
    };
    const alaTecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Cierra la paleta y nada más: `document` escucha antes que la ventana, que en el
      // selector de fuentes lo cerraría entero.
      e.stopPropagation();
      setPaletaDe(null);
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', alaTecla);
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', alaTecla);
    };
  }, [paletaDe]);

  useEffect(() => {
    if (creando) inputAlta.current?.focus();
  }, [creando]);
  useEffect(() => {
    if (renombrandoId) inputRenombre.current?.select();
  }, [renombrandoId]);

  const confirmarAlta = async () => {
    const nombre = nombreNuevo.trim();
    if (!nombre || guardando) return;
    setGuardando(true);
    try {
      await onCrear(nombre);
      setNombreNuevo('');
      setCreando(false);
    } finally {
      setGuardando(false);
    }
  };

  const confirmarRenombre = async (id: string) => {
    const nombre = borrador.trim();
    if (!nombre) return;
    setRenombrandoId(null);
    await onRenombrar(id, nombre);
  };

  return (
    <>
      <div className={grillaClassName}>
        {/* Nueva carpeta — primera de la grilla, como la card de "Agregar
            modelo" del tab de al lado: la acción vive DONDE está el contenido. */}
        <div className="flex flex-col">
          {creando ? (
            <div className="flex w-full flex-col">
              <div className="pointer-events-none opacity-60">
                <IconoCarpeta portada={[]} />
              </div>
              <div className="mt-2 flex items-center gap-1">
                <input
                  ref={inputAlta}
                  value={nombreNuevo}
                  onChange={(e) => setNombreNuevo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmarAlta();
                    if (e.key === 'Escape') {
                      // Cancela el nombre y nada más: adentro del selector de fuentes, el
                      // Escape que llega a la ventana lo cierra entero.
                      e.stopPropagation();
                      setCreando(false);
                      setNombreNuevo('');
                    }
                  }}
                  // Sin `onBlur` que confirme: al tocar el tilde el input pierde
                  // el foco primero y la carpeta se creaba dos veces.
                  placeholder="Nombre"
                  maxLength={60}
                  className="min-w-0 flex-1 rounded-md border border-[#1644EB66] bg-white px-2 py-1 text-[13px] text-brand-black outline-none focus:border-brand-primary"
                />
                <button
                  type="button"
                  onClick={confirmarAlta}
                  disabled={!nombreNuevo.trim() || guardando}
                  aria-label="Crear carpeta"
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-brand-primary transition-colors hover:bg-[#EBEFFE] disabled:opacity-40"
                >
                  {guardando ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCreando(true)}
              className="group flex w-full flex-col text-left"
            >
              <div className="flex aspect-[4/3] w-full items-center justify-center rounded-[12px] border border-dashed border-black/15 bg-white transition-colors group-hover:border-[#1644EB80] group-hover:bg-[#EBEFFE]">
                <FolderPlus
                  className="h-7 w-7 text-[#02062B4D] transition-colors group-hover:text-brand-primary"
                  strokeWidth={1.5}
                />
              </div>
              <p className="mt-2 truncate text-[13.5px] font-medium text-[#02062BCC]">
                Nueva carpeta
              </p>
              <p className="text-[12px] text-[#02062B66]">
                Para tus referencias
              </p>
            </button>
          )}
        </div>

        {carpetas.map((carpeta) => {
          const renombrando = renombrandoId === carpeta.id;
          return (
            <div key={carpeta.id} className="group relative flex flex-col">
              <button
                type="button"
                onClick={() => onAbrir(carpeta)}
                aria-label={`Abrir ${carpeta.nombre}`}
                className="w-full rounded-[12px] transition-transform active:scale-[0.98]"
              >
                <IconoCarpeta portada={carpeta.portada} color={carpeta.color} />
              </button>

              {/* Paleta, lápiz y tacho. Aparecen al hover para no ensuciar la grilla;
                  mientras la paleta de ESTA carpeta está abierta se quedan visibles,
                  porque el mouse se va del ícono al elegir el color. */}
              {/* Ya adjunta al chat: se marca y no se ofrece adjuntar de nuevo. */}
              {adjuntada?.(carpeta.id) && (
                <span className="pointer-events-none absolute left-1.5 top-1.5 rounded-full bg-[#E9EDFD] px-2 py-0.5 text-[11px] font-semibold text-brand-primary">
                  {rotuloAdjuntada(carpeta)}
                </span>
              )}
              {!renombrando && (
                <div
                  className={`absolute right-1 top-1 flex gap-1 transition-opacity ${
                    paletaDe === carpeta.id
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
                  }`}
                >
                  {onAdjuntar && !adjuntada?.(carpeta.id) && (
                    <button
                      type="button"
                      onClick={() => onAdjuntar(carpeta)}
                      aria-label={`${tituloAdjuntar}: ${carpeta.nombre}`}
                      title={tituloAdjuntar}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-[#02062BB3] shadow-sm backdrop-blur transition-colors hover:text-brand-primary"
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      setPaletaDe((abierta) =>
                        abierta === carpeta.id ? null : carpeta.id,
                      )
                    }
                    aria-label={`Cambiar el color de ${carpeta.nombre}`}
                    aria-expanded={paletaDe === carpeta.id}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-[#02062BB3] shadow-sm backdrop-blur transition-colors hover:text-brand-primary"
                  >
                    <Palette className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBorrador(carpeta.nombre);
                      setRenombrandoId(carpeta.id);
                    }}
                    aria-label={`Renombrar ${carpeta.nombre}`}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-[#02062BB3] shadow-sm backdrop-blur transition-colors hover:text-brand-primary"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setABorrar(carpeta)}
                    aria-label={`Eliminar ${carpeta.nombre}`}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-[#02062BB3] shadow-sm backdrop-blur transition-colors hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* La paleta. `absolute` dentro de la card y no un portal: la columna de
                  contenido no recorta, y un `fixed` habría que reposicionarlo a cada
                  scroll del panel (el scroller de desktop no es el body, ADR-171). */}
              {paletaDe === carpeta.id && (
                <div
                  ref={paleta}
                  className="absolute right-1 top-9 z-20 grid grid-cols-4 gap-1.5 rounded-xl border border-black/[0.07] bg-white p-2 shadow-lg"
                  role="group"
                  aria-label="Color de la carpeta"
                >
                  {COLORES_DE_CARPETA.map((c) => {
                    const elegido = colorDeCarpeta(carpeta.color).id === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setPaletaDe(null);
                          void onCambiarColor(carpeta.id, c.id);
                        }}
                        aria-label={c.etiqueta}
                        aria-pressed={elegido}
                        title={c.etiqueta}
                        className={`inline-flex h-6 w-6 items-center justify-center rounded-full transition-transform hover:scale-110 ${
                          elegido ? 'ring-2 ring-brand-primary ring-offset-1' : ''
                        }`}
                        style={{ backgroundColor: c.frente }}
                      >
                        {elegido && (
                          <Check className="h-3.5 w-3.5 text-[#02062BCC]" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {renombrando ? (
                <div className="mt-2 flex items-center gap-1">
                  <input
                    ref={inputRenombre}
                    value={borrador}
                    onChange={(e) => setBorrador(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmarRenombre(carpeta.id);
                      if (e.key === 'Escape') {
                        e.stopPropagation();
                        setRenombrandoId(null);
                      }
                    }}
                    maxLength={60}
                    className="min-w-0 flex-1 rounded-md border border-[#1644EB66] bg-white px-2 py-1 text-[13px] text-brand-black outline-none focus:border-brand-primary"
                  />
                  <button
                    type="button"
                    onClick={() => confirmarRenombre(carpeta.id)}
                    disabled={!borrador.trim()}
                    aria-label="Guardar nombre"
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-brand-primary transition-colors hover:bg-[#EBEFFE] disabled:opacity-40"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setRenombrandoId(null)}
                    aria-label="Cancelar"
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#02062B80] transition-colors hover:bg-black/[0.04]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onAbrir(carpeta)}
                  className="mt-2 text-left"
                >
                  <p className="truncate text-[13.5px] font-medium text-[#02062BE6]">
                    {carpeta.nombre}
                  </p>
                  <p className="text-[12px] text-[#02062B66]">
                    {contador(carpeta.cantidadFotos)}
                  </p>
                </button>
              )}
            </div>
          );
        })}
      </div>

      <ModalConfirmar
        abierto={aBorrar !== null}
        titulo={`Eliminar "${aBorrar?.nombre ?? ''}"`}
        detalle={
          aBorrar && aBorrar.cantidadFotos > 0
            ? `Se borran también las ${aBorrar.cantidadFotos} fotos que tiene adentro. No se puede deshacer.`
            : 'No se puede deshacer.'
        }
        confirmar="Eliminar carpeta"
        trabajando={borrando}
        onCancelar={() => setABorrar(null)}
        onConfirmar={async () => {
          if (!aBorrar) return;
          setBorrando(true);
          try {
            await onEliminar(aBorrar.id);
            setABorrar(null);
          } finally {
            setBorrando(false);
          }
        }}
      />
    </>
  );
};

export default GrillaDeCarpetas;
